/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */
import { parseTenor, addTermToDate2, getYearFromDate } from '@/helper/irs.helper.service';

const mayBeCdsPlot = (text) => {
    const tokens = text.split(/\s+/).slice(1); // skip 'plot'
    return tokens.some((token) => /^(\d+|\d*\.\d+)[mywd]$/.test(token));
};

const mayBeFwdCdsPlot = (text) => {
    const tokens = text.split(/\s+/).slice(1); // skip 'plot'
    return tokens.some((token) => /(?:\d*(?:\.*\d+)[mywd]){2,}/.test(token));
};

const sortLegPairs = (legPairs) => {
    if (legPairs.length !== 2 && legPairs.length !== 3) return legPairs.flat();

    const pairs = legPairs.map((x) => {
        const start = addTermToDate2(x[0]);

        return {
            pair: x,
            start: getYearFromDate(start),
            end: getYearFromDate(addTermToDate2(x[1], start))
        };
    });

    const pairs2 = pairs.toSorted(({ start: start0, end: end0 }, { start: start1, end: end1 }) => {
        if (end0 === end1) return start1 - start0;
        return end1 - end0;
    });

    return pairs2.map((x) => x.pair).flat();
};

const getPlotType = ({ isRoll, fullTenors }) => {
    let plotType = '';
    if (isRoll) return 'immRoll';
    if (fullTenors.length === 6) plotType = 'fBfly';
    else if (fullTenors.length === 4) plotType = 'fSpread';

    return plotType;
};

const checkForImmFwdPlot = (str, validImm, isIrsPage) => {
    const splitStartRegex = /^[zmuh][0-9]{1,2}$/i;
    const fullTokenRegex = /^([zmuh][0-9]{2})((?:[1-9]\d?(?:\.\d{1,2})?|\.\d{1,2})[dwmy])$/i;
    const oldFullTokenRegex = /^([zmuh][6-9])((?:[1-9]\d?(?:\.\d{1,2})?|\.\d{1,2})[dwmy])$/;
    const tenorOnlyRegex = /^(?:\d+|\d*\.\d+)[dwmy]$/i;
    // Forward-only leg like 1y1y, 3y4y, 0.5y2y
    const fwdLegRegex = /^((?:\d+|\d*\.\d+)[dwmy])((?:\d+|\d*\.\d+)[dwmy])$/i;

    let tokens = str.trim().toLowerCase().replace(/fwd/g, '').split(/\s+/);
    const isRoll = tokens.includes('roll');
    const isPlotA = tokens.includes('plota');
    const isBasis6s3s = tokens.includes('6s3s') || tokens.includes('63');
    const isBob = tokens.includes('bob');
    const nameStartIdx = ['plot', 'roll', 'plota', '6s3s', '63', 'bob'].includes(tokens[0]) ? 1 : 0;
    if (isRoll && tokens[0] !== 'roll') tokens = tokens.filter((t) => t !== 'roll');

    const phrase = tokens.slice(nameStartIdx);
    const tenorStartIdx = phrase.findIndex((token) => splitStartRegex.test(token) || fullTokenRegex.test(token) || oldFullTokenRegex.test(token) || fwdLegRegex.test(token));

    if (tenorStartIdx === -1) return { isValid: false };

    const name = phrase.slice(0, tenorStartIdx).join(' ');
    if (!name && !isIrsPage) return { isValid: false };

    const args = phrase.slice(tenorStartIdx);

    // Case 1: Split format (e.g., z5 2y 5y)
    if (splitStartRegex.test(args[0])) {
        const start = args[0];
        const imm = validImm.find((e) => e.label.toLowerCase() === start || e.oldLabel?.toLowerCase() === start);
        if (!imm) return { isValid: false };
        const immYear = `${imm.y}y`;

        let tenorStartIndex = 1;

        let secondImm = null;
        if (isRoll) {
            if (splitStartRegex.test(args[1])) {
                secondImm = validImm.find((i) => i.label.toLowerCase() === args[1] || i.oldLabel.toLowerCase() === args[1]);
                tenorStartIndex += 1;
            } else {
                secondImm = validImm.find((i) => i.y > imm.y);
            }
        }
        const tenors = args.slice(tenorStartIndex).sort((a, b) => parseTenor(b) - parseTenor(a));

        const isValid = tenors.length >= 1
            && tenors.length <= 3
            && tenors.every((t) => tenorOnlyRegex.test(t));
        const fullTenors = tenors.map((t) => [immYear, t]).flat();
        if (!isRoll && fullTenors.length >= 4) secondImm = imm;
        return isValid
            ? {
                isValid,
                tenors: fullTenors,
                name,
                isFwd: true,
                imm,
                secondImm,
                thirdImm: (!isRoll && fullTenors.length === 6) ? imm : null,
                plotType: getPlotType({ isRoll, fullTenors }),
                isPlotA,
                isBasis6s3s,
                isBob
            }
            : { isValid: false };
    }

    // Normalize plain tenors ONLY for Case 2
    const normalizeTenorsForCase2 = (args) => args.map((t) => (tenorOnlyRegex.test(t) ? `0y${t}` : t));

    const normalizedArgs = normalizeTenorsForCase2(args);

    // Case 2: Full token format (e.g., z55y z56y)
    if (fullTokenRegex.test(normalizedArgs[0]) || oldFullTokenRegex.test(normalizedArgs[0]) || fwdLegRegex.test(normalizedArgs[0])) {
        if (normalizedArgs.length > 3) return { isValid: false };
        let isHybrid = false;
        const tenors = [];
        const immTenors = [];
        for (let i = 0; i < normalizedArgs.length; i++) {
            const match = normalizedArgs[i].match(oldFullTokenRegex) ?? normalizedArgs[i].match(fullTokenRegex);
            if (match) {
                const [, immTerm, tenor] = match;
                const imm = validImm.find(
                    (e) => e.label.toLowerCase() === immTerm
                        || e.oldLabel?.toLowerCase() === immTerm
                );
                if (!imm) return { isValid: false };

                tenors.push([`${imm.y}y`, tenor]);
                immTenors.push(imm);
            } else if (normalizedArgs[i].match(fwdLegRegex)) {
                isHybrid = true;
                const [, start, end] = normalizedArgs[i].match(fwdLegRegex);
                tenors.push([start, end]);
            } else return { isValid: false };
        }

        if (isHybrid && !immTenors.length) return { isValid: false };

        const arrayToObject = (arr, lookupArray) => {
            const keys = arr.length === 6 ? ['thirdImm', 'secondImm', 'imm'] : ['secondImm', 'imm'];
            const obj = {};

            for (let i = 0; i < arr.length; i += 2) {
                const found = lookupArray.find((item) => arr[i] === `${item.y}y`);
                obj[keys[i / 2]] = found || null;
            }

            return obj;
        };

        const sorted = sortLegPairs(tenors);
        const immTenors2 = [sorted[4], sorted[2], sorted[0]].map((s) => (s ? immTenors.find((it) => Number(s.substring(0, s.length - 1)) === it.y) : undefined)).filter(Boolean);
        return {
            isValid: true,
            tenors: sorted,
            name,
            isFwd: true,
            ...(isHybrid ? arrayToObject(sorted, immTenors2) : {
                imm: immTenors2[0],
                secondImm: immTenors2[1],
                thirdImm: immTenors2[2]
            }),
            plotType: tenors.length === 3 ? 'fBflyDiff' : tenors.length === 2 ? 'fSpreadDiff' : '',
            isPlotA,
            isBasis6s3s,
            isBob
        };
    }

    return { isValid: false };
};

const getFwdRegex = (allowDecimal) => (allowDecimal
    ? /^plot(?:\s+([a-z\s]+?))?\s+((?:\d+(?:\.\d+)?|\.\d+)[ywdm](?:\d+(?:\.\d+)?|\.\d+)[ywdm](?:\s+(?:\d+(?:\.\d+)?|\.\d+)[ywdm](?:\d+(?:\.\d+)?|\.\d+)[ywdm])*)$/i
    : /^plot(?:\s+([a-z ]+))?\s+(\S*(?:\d+[ywdm]\d+[ywdm]\s){1,3}\S*(?:\s+\S+)*)$/i);

const getValidFwdRegex = (allowDecimal) => (allowDecimal
    ? /^plot(?:\s+([a-z]+(?:\s+[a-z]+)?))?\s+((?:\d+(?:\.\d+)?|\.\d+)[ywdm])((?:\d+(?:\.\d+)?|\.\d+)[ywdm])(?:\s+((?:\d+(?:\.\d+)?|\.\d+)[ywdm])((?:\d+(?:\d+)?|\.\d+)[ywdm]))?(?:\s+((?:\d+(?:\.\d+)?|\.\d+)[ywdm])((?:\d+(?:\.\d+)?|\.\d+)[ywdm]))?$/i
    : /^plot(?:\s+([a-z]+))?\s+(\d+[ywdm])(\d+[ywdm])(?:\s+(\d+[ywdm])(\d+[ywdm]))?(?:\s+(\d+[ywdm])(\d+[ywdm]))?$/i);

const getPlotPattern = (allowDecimal) => (allowDecimal
    ? /^plot\s+(.*?)((?:\d+(\.\d+)?\s*(?:[mywd])?\s?(?:\s+f(?:wd)?)?\s?)+)(spot)?$/
    : /^plot\s+(.*?)((?:\d+\s*(?:[mywd])?\s?(?:\s+f(?:wd)?)?\s?)+)(spot)?$/);

const getGeneralPattern = (allowDecimal) => (allowDecimal
    ? /^(.+?)\s((?:\d*(\.\d+)?\s*(?:[mywd])?\s?(?:\s+f(?:wd)?)?\s?)*)(spot)?$/
    : /^(.+?)\s((?:\d+\s*(?:[mywd])?\s?(?:\s+f(?:wd)?)?\s?)*)(spot)?$/);

const getRollPattern = (allowDecimal) => (allowDecimal
    ? /^roll\s+(.*?)((?:\d+(\.\d+)?\s*(?:[mywd])?\s?(?:\s+f(?:wd)?)?\s?)+)(spot)?$/
    : /^roll\s+(.*?)((?:\d+\s*(?:[mywd])?\s?(?:\s+f(?:wd)?)?\s?)+)(spot)?$/);

const matchAndNormalizeTenor = (text, allowDecimal = true) => {
    const regex = getGeneralPattern(allowDecimal);
    const result = text.match(regex);
    if (!result) return null;

    if (result[2]) {
        result[2] = result[2]
            .replace(/(?:^|\s)(\.\d+[mywd])/g, (match, g1) => ` 0${g1}`)
            .trim();
        result[0] = `${result[1]} ${result[2]}`;
        result[1] = result[1].replace(/^(?:fwcm|basis|plot)\s*/i, '').trim();
    }
    return result;
};

const checkForHybrid = ({
    trimmedText, isPlotA, isPlotF, isBasis6s3s, isBob
}) => {
    const tokens = trimmedText
        .replace(/^plot(?:\s+[a-z\s]+)?\s+/i, '')
        .trim()
        .split(/\s+/);

    if (tokens.length === 1) return { isValid: false };

    const legPairs = [];
    const singleTenorRegex = /^(\d+(?:\.\d+)?|\.\d+)([ywdm])$/i;
    const pairRegex = /^(\d+(?:\.\d+)?|\.\d+)([ywdm])(\d+(?:\.\d+)?|\.\d+)([ywdm])$/i;
    let totalSingleMatches = 0;
    for (const token of tokens) {
        const pairMatch = token.match(pairRegex);
        const singleMatch = token.match(singleTenorRegex);
        if (pairMatch) {
            legPairs.push([`${pairMatch[1]}${pairMatch[2]}`, `${pairMatch[3]}${pairMatch[4]}`]);
        } else if (singleMatch) {
            totalSingleMatches++;
            const unit = singleMatch[2];
            legPairs.push(['0y', `${singleMatch[1]}${unit}`]);
        } else return { isValid: false };
    }

    if (tokens.length === totalSingleMatches) return { isValid: false }; // Probable spot pattern

    if (legPairs.length) {
        const pairs = sortLegPairs(legPairs);
        return {
            name: '',
            tenors: pairs,
            isFwd: true,
            spot: false,
            isValid: true,
            isPlotF,
            isPlotA,
            isBasis6s3s,
            isBob,
            plotType: getPlotType({
                isRoll: false,
                fullTenors: pairs.flat()
            })
        };
    }

    return { isValid: false };
};
const isMeetPattern = (str) => /^PLOT (JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\d{2}\s[A-Za-z]+$/.test(str);


const parsePlot = (text, validImm, isIrsPage) => {
    const trimmedOriginal = text.trim();
    const normalizedText = trimmedOriginal.toLowerCase();

    const isPlotF = /^plotf\b/i.test(normalizedText);
    const isPlotA = /^plota\b/i.test(normalizedText);
    const isBasis6s3s = /^(6s3s|63)\b/i.test(normalizedText);
    const isBob = /^(bob)\b/i.test(normalizedText);

    let trimmedText = normalizedText;
    if (isPlotF || isPlotA) trimmedText = normalizedText.replace(/^plot(f|a)\b/i, 'plot');
    if (isBasis6s3s) trimmedText = normalizedText.replace(/^(6s3s|63)\b/i, 'plot');
    if (isBob) trimmedText = normalizedText.replace(/^bob\b/i, 'plot');

    if (isMeetPattern(trimmedText.toUpperCase())) return { isValid: true, isMeet: true, tenors: [], isFwd: true, label: trimmedText.toUpperCase().replace('PLOT ', '') };
    const result = checkForImmFwdPlot(normalizedText, validImm, isIrsPage);
    if (result.isValid) return result;

    const checkIfMaybeCds = mayBeCdsPlot(trimmedText);
    const checkIfMaybeFwdCds = mayBeFwdCdsPlot(trimmedText);
    const newFwdregex = getFwdRegex(checkIfMaybeCds || checkIfMaybeFwdCds);

    if (newFwdregex.test(trimmedText)) {
        const validRegex = getValidFwdRegex(checkIfMaybeCds || checkIfMaybeFwdCds);
        const match = trimmedText.match(validRegex);

        if (match) {
            const [, name, y1, y2, y3, y4, y5, y6] = match;
            const result = [[y1, y2]];
            if (y3 && y4) result.push([y3, y4]);
            if (y5 && y6) result.push([y5, y6]);
            const pairs = sortLegPairs(result);
            return {
                name: name || '',
                tenors: pairs,
                isFwd: true,
                spot: false,
                isValid: true,
                isPlotF,
                isPlotA,
                plotType: getPlotType({
                    isRoll: false,
                    fullTenors: pairs.flat()
                }),
                isBasis6s3s,
                isBob
            };
        }
        return { isValid: false };
    }

    const hybridResult = checkForHybrid({
        trimmedText, isPlotA, isPlotF, isBasis6s3s, isBob
    });
    if (hybridResult.isValid && !isPlotF) return hybridResult;

    const plotPattern = getPlotPattern(checkIfMaybeCds);
    const rollPattern = getRollPattern(checkIfMaybeCds);
    const generalPattern = getGeneralPattern(checkIfMaybeCds);

    let match = trimmedText.match(generalPattern);
    const plotMatch = plotPattern.test(trimmedText);
    const rollMatch = rollPattern.test(trimmedText);
    if (plotMatch) match = trimmedText.match(plotPattern);
    if (rollMatch) match = trimmedText.match(rollPattern);
    const containsRollCmd = !rollMatch && trimmedText.includes('roll');
    if (containsRollCmd) match = trimmedText.replaceAll('roll', '').replace(/\s+/g, ' ').match(plotPattern);
    if (plotMatch) {
        match = matchAndNormalizeTenor(trimmedText);
    }

    if (!match && !plotMatch && !rollMatch) return { isValid: false };
    const isRoll = rollPattern.test(trimmedText) || (plotMatch && containsRollCmd);

    let name = match?.[1]?.trim() ?? '';
    let spot = false;
    if (match?.[3] === 'spot') spot = true;
    if (name.endsWith('spot')) {
        if (spot) return { isValid: false };
        name = name.substring(0, name.length - 4).trim();
        spot = true;
    }
    let fwdTenor = null;

    const terms = match?.[2].trim().split(/\s+/);
    if (!terms) return { isValid: false };
    const checkForForward = (t) => t === 'fwd' || t === 'f';
    const isFwd = Boolean(terms?.find(checkForForward));
    const tenors = terms.filter((t) => !checkForForward(t)).map((t) => (/^\d+$/.test(t) ? `${t}y` : t));

    const isValidTenors = tenors.every((tenor) => (checkIfMaybeCds ? /^\d+(\.\d+)?[mywd]$/.test(tenor) : /^\d+[mywd]$/.test(tenor)));
    if (!isValidTenors) return { isValid: false };
    const cleanedTenors = tenors.slice(isFwd ? 1 : 0).sort((a, b) => parseTenor(b) - parseTenor(a));
    if (isFwd) [fwdTenor] = tenors;

    const isValidLength = isFwd ? [1, 2, 3].includes(cleanedTenors.length) : (spot && cleanedTenors.length === 1)
        || (!spot && (plotMatch || rollMatch) && !name)
        || cleanedTenors.length >= 1;

    if (!isValidLength) return { isValid: false };

    if (isRoll) {
        const [imm, secondImm] = validImm;

        return {
            name,
            tenors: cleanedTenors.flatMap((t) => [`${imm.y}y`, t]),
            isFwd: true,
            spot,
            isValid: true,
            checkIfMaybeCds,
            isRoll,
            imm,
            secondImm,
            isPlotF,
            isPlotA,
            plotType: 'immRoll',
            isBasis6s3s,
            isBob
        };
    }
    let plotType = '';
    if (isFwd && cleanedTenors.length >= 2 && cleanedTenors.length <= 3) plotType = cleanedTenors.length === 2 ? 'fSpread' : 'fBfly';

    return {
        name,
        tenors: isFwd ? cleanedTenors.flatMap((t) => [fwdTenor, t]) : cleanedTenors,
        isFwd,
        spot,
        isValid: true,
        checkIfMaybeCds,
        isRoll,
        isPlotF,
        isPlotA,
        isBasis6s3s,
        isBob,
        plotType
    };
};

const TENOR_REGEX = /^(?:\d+(?:\.\d+)?[dwmy])+$/i;
const RANGE_REGEX = /^(\d+(?:\.\d+)?)(d|w|m|y)-(\d+(?:\.\d+)?)(w|m|y)$/;
const VALID_CHRONOS_REGEX = /^(?:\*|\d+(?:\.\d+)?[dwmy](?:(?:\s*,\s*|\s+(?!FWD)|)\d+(?:\.\d+)?[dwmy])*|\d+(?:\.\d+)?[dwmy]-\d+(?:\.\d+)?[dwmy])(?:\s+FWD\s+(?:\*|\d+(?:\.\d+)?[dwmy](?:(?:\s*,\s*|\s+|)\d+(?:\.\d+)?[dwmy])*|\d+(?:\.\d+)?[dwmy]-\d+(?:\.\d+)?[dwmy]))?$/;
const INVALID_FWD_WITHOUT_SPOT_REGEX = /^chronos(?:\s+[a-z]+(?:\s+[a-z]+)*)?\s+fwd\b/i;
const INVALID_FWD_WITHOUT_TARGET_REGEX = /\bfwd\b\s*$/i;
const INVALID_CHRONOS_GARBAGE_REGEX = /\bchronos\b\s+(?!((\d+(?:\.\d+)?[ymwd])|([zmuh]\d{1,2})|(fwd|spot|x)|\*|[, ])+)/i;
const STRUCTURE_SPLIT_PATTERN = /^(.*?(?:\([^)]*\)\s*)*)(?=\s(?:\*|\d+(?:\.\d+)?[ymwd](?:-\d+(?:\.\d+)?[ymwd])?(?:,\d+(?:\.\d+)?[ymwd])?|\d+(?:\.\d+)?[ymwd]\s+fwd))/i;
const CHRONOS_COMPACT_REGEX = /^chronos(?:\s+([a-z]+(?:\s+[a-z]+)*))?\s+(?:((?:\d+(?:\.\d+)?[dmyw](?:[zmuh]\d{1,2}|\d+(?:\.\d+)?[dmyw])(?:\s*,\s*)?)+)|((?:[zmuh]\d{1,2})|\d+(?:\.\d+)?[dmyw])(\d+(?:\.\d+)?[dmyw]))$/i;
const TENOR_MATCH_REGEX = /^(\d+(?:\.\d+)?)([dwmy])$/i;
const CHRONOS_EXTRACT_REGEX = /^chronos\s+(.+?)(?:\s+fwd\b|$)/i;
const FWD_SPLIT_REGEX = /\s+fwd\s+/i;
const COMPACT_PAIR_REGEX = /^(\d+(?:\.\d+)?[dmyw])((?:[zmuh]\d{1,2})|\d+(?:\.\d+)?[dmyw])$/;
const FWD_MATCH_REGEX = /\bchronos\b\s+(.+?)\s+fwd\s+(.+)$/i;
const SPREAD_DETECT_REGEX = /(\d+(?:\.\d+)?[ymwd])\s+(\d+(?:\.\d+)?[ymwd])/i;
const X_SEPARATOR_REGEX = /(?<=\d+(?:\.\d+)?[ymwd])\s*x\s*(?=\d+(?:\.\d+)?[ymwd])/gi;
const HAS_ALL_REGEX = /\ball\b/i;
const isMeetingDatePattern = (str) => /^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|RBA|FED|BOC|SNB|ECB|BOE|BOJ|NZD|MEET)(\d{2})?\s*[A-Za-z]*$/i
    .test(str);

const UNIT_ORDER = ['d', 'w', 'm', 'y'];
const SPOT_VALUES = new Set(['0d', '0m', '0y', 'spot']);
const UNIT_TO_MONTHS = {
    d: 1 / 30,
    w: 7 / 30,
    m: 1,
    y: 12
};
const UNIT_TO_DAYS = {
    d: 1, w: 7, m: 365 / 12, y: 365
};

const tenorCache = new Map();

const isSpotValue = (v) => SPOT_VALUES.has(v?.toLowerCase());
const isTenorValue = (v) => TENOR_REGEX.test(v);

const tenorToDays = (tenor) => {
    if (isSpotValue(tenor)) return 0;

    const cached = tenorCache.get(tenor);
    if (cached !== undefined) return cached;

    const match = tenor.match(/^(\d+(?:\.\d+)?)([dwmy])$/i);
    if (!match) return 0;

    const days = parseFloat(match[1]) * (UNIT_TO_DAYS[match[2].toLowerCase()] || 0);
    tenorCache.set(tenor, days);
    return days;
};

const immToDays = (imm) => {
    if (!imm) return 0;
    return imm.days ?? (imm.y ? imm.y * 365 : 0);
};

const createImmStructures = (() => {
    const cache = new WeakMap();

    return (validImm) => {
        if (cache.has(validImm)) return cache.get(validImm);

        const labels = validImm.flatMap(({ label, oldLabel }) => [label?.toLowerCase(), oldLabel?.toLowerCase()].filter(Boolean));

        const immSet = new Set(labels);

        const immMap = new Map(
            validImm.flatMap((item) => {
                const entries = [];
                if (item?.label) entries.push([item.label.toLowerCase(), item]);
                if (item?.oldLabel) entries.push([item.oldLabel.toLowerCase(), item]);
                return entries;
            })
        );

        const result = { immSet, immMap, labels };
        cache.set(validImm, result);
        return result;
    };
})();

const normalizeRange = (str) => str.replace(/\s*-\s*/g, '-');

const validateRange = (rangeStr) => {
    const normalized = normalizeRange(rangeStr);
    const match = normalized.match(RANGE_REGEX);
    if (!match) return false;
    const [, startNum, startUnit, endNum, endUnit] = match;
    const start = parseFloat(startNum);
    const end = parseFloat(endNum);
    if (Number.isNaN(start) || Number.isNaN(end)) return false;

    const startInMonths = start * UNIT_TO_MONTHS[startUnit];
    const endInMonths = end * UNIT_TO_MONTHS[endUnit];

    return startInMonths <= endInMonths;
};

const isValidChronosValue = (raw) => {
    if (!raw || !VALID_CHRONOS_REGEX.test(raw)) return false;
    if (raw === '*') return true;

    const value = raw.trim();
    const hasDash = value.includes('-');
    const hasComma = value.includes(',');
    const hasSpace = /\s/.test(value);

    if (hasDash) return validateRange(value);
    if (hasComma || hasSpace) {
        return value.split(/[,\s]+/).filter(Boolean).every((p) => TENOR_REGEX.test(p));
    }
    return !SPOT_VALUES.has(raw.toLowerCase()) && TENOR_REGEX.test(raw);
};

const expandValues = (value, { allowImm = false, validImm = [], isSpread = false } = {}) => {
    const normalized = isSpread ? value.trim().toLowerCase() : value.replace(/\s+/g, '').toLowerCase();

    if (normalized === '*') {
        return ['*'];
    }

    if (normalized.includes(',')) return normalized.split(',');

    if (normalized.includes('-')) {
        const [start, end] = normalized.split('-');
        const startMatch = start.match(TENOR_MATCH_REGEX);
        const endMatch = end.match(TENOR_MATCH_REGEX);
        if (!startMatch || !endMatch) return [];

        const startNum = parseFloat(startMatch[1]);
        const endNum = parseFloat(endMatch[1]);
        const startUnit = startMatch[2].toLowerCase();
        const endUnit = endMatch[2].toLowerCase();

        if (startUnit === endUnit && Number.isInteger(startNum) && Number.isInteger(endNum)) {
            return Array.from({ length: endNum - startNum + 1 }, (_, i) => `${startNum + i}${startUnit}`);
        }

        const startIdx = UNIT_ORDER.indexOf(startUnit);
        const endIdx = UNIT_ORDER.indexOf(endUnit);
        if (startIdx === -1 || endIdx === -1 || startIdx > endIdx) return [];

        return [start, end];
    }

    if (allowImm) {
        const { immSet } = createImmStructures(validImm);
        if (immSet.has(normalized)) return [normalized];
    }

    if (isSpread && /\s/.test(normalized)) return normalized.split(/\s+/);

    return [normalized];
};

const getTenorRange = (tenors, validImm = []) => {
    if (tenors[0] === '*') {
        return { minDays: 0, maxDays: 0, isAllFwd: true };
    }
    const { immMap } = createImmStructures(validImm);

    let minDays = Infinity;
    let maxDays = -Infinity;

    for (const tenor of tenors) {
        const tenorLower = tenor.toLowerCase();
        const days = immMap.has(tenorLower)
            ? immToDays(immMap.get(tenorLower))
            : tenorToDays(tenor);

        if (days < minDays) minDays = days;
        if (days > maxDays) maxDays = days;
    }

    return { minDays: minDays - 1, maxDays: maxDays + 1, isAllFwd: false };
};

const parseChronos = (command, validImm = [], isSpread = false) => {
    const { immSet, immMap } = createImmStructures(validImm);
    const chronosMatch = command.match(CHRONOS_EXTRACT_REGEX);

    if (!chronosMatch) return [{ isValid: false }];

    const rawChronos = chronosMatch[1];
    if (!isValidChronosValue(rawChronos)) {
        return [{ isValid: false }];
    }

    const [tenorPart, fwdPart] = command.split(FWD_SPLIT_REGEX);

    const chronosList = [...new Set(expandValues(rawChronos, { isSpread }))];
    if (chronosList.some(isSpotValue)) return [{ isValid: false }];
    const spreadTenor = isSpread ? chronosList.map((t) => t.toUpperCase()).join('x') : '';

    const hasCommaBeforeFwd = tenorPart.includes(',');
    const hasCommaAfterFwd = fwdPart?.includes(',') || false;
    const fwdMatch = command.match(/\b(fwd|forward)\s*([^\s]+)/i);
    const fwdList = fwdMatch ? expandValues(fwdMatch[2], { allowImm: true, allowSpot: true, validImm }) : [];
    const hasSpotFwd = fwdList.length === 1 && fwdList.some(isSpotValue);

    const getBounds = (list) => (list[0] === '*' ? { minDays: 0, maxDays: 0, isAllFwd: true } : getTenorRange(list, validImm));

    const buildTenorResult = (tenors, minDays, maxDays, isAllTenor) => ({
        tenors,
        isFwd: false,
        isImm: false,
        isValid: true,
        isSpot: true,
        imm: null,
        startTenorList: {
            fwdStart: 0,
            fwdEnd: 0,
            tenorStart: minDays,
            tenorEnd: maxDays,
            isSpread,
            spreadTenor,
            isAllTenor
        }
    });

    if (!fwdMatch || hasSpotFwd) {
        if (hasCommaBeforeFwd) {
            return chronosList.map((item) => {
                const { minDays, maxDays, isAllFwd } = getBounds([item]);
                return buildTenorResult(item, minDays, maxDays, isAllFwd);
            });
        }

        const { minDays, maxDays, isAllFwd } = getBounds(chronosList);
        return [buildTenorResult(chronosList, minDays, maxDays, isAllFwd)];
    }

    if (fwdList.some((fwd) => fwd !== '*' && !isTenorValue(fwd) && !immSet.has(fwd.toLowerCase()))) {
        return [{ isValid: false }];
    }

    if (fwdList.length === 0) return [];

    const result = [];

    const pushResult = ({
        tenors, fwdTenors, tenorBounds, fwdBounds, fwdStartOverride
    }) => {
        const immVal = !Array.isArray(fwdTenors) ? fwdTenors.toLowerCase() : fwdTenors[0].toLowerCase();
        const hasImmFwd = Boolean(immSet.has(immVal));
        const immValue = hasImmFwd ? immMap.get(immVal) : null;
        const isAllFwd = fwdBounds?.isAllFwd;
        const isAllTenor = tenorBounds?.isAllFwd;

        result.push({
            tenors,
            fwdTenors,
            isFwd: true,
            isImm: hasImmFwd,
            imm: immValue,
            isValid: true,
            isSpot: false,
            startTenorList: {
                fwdStart: fwdStartOverride ?? fwdBounds.minDays,
                fwdEnd: fwdBounds.maxDays,
                tenorStart: tenorBounds.minDays,
                tenorEnd: tenorBounds.maxDays,
                isSpread,
                spreadTenor,
                isAllFwd,
                isAllTenor
            }
        });
    };

    if (hasCommaBeforeFwd && hasCommaAfterFwd) {
        for (const fwd of fwdList) {
            const fwdBounds = getTenorRange([fwd], validImm);
            for (const tenor of chronosList) {
                pushResult({
                    tenors: tenor,
                    fwdTenors: fwd,
                    tenorBounds: getBounds([tenor]),
                    fwdBounds,
                    fwdStartOverride: fwd === '*' ? 0 : fwdBounds.minDays
                });
            }
        }
    } else if (hasCommaBeforeFwd) {
        const fwdBounds = getTenorRange(fwdList, validImm);
        for (const tenor of chronosList) {
            pushResult({
                tenors: tenor,
                fwdTenors: fwdList,
                tenorBounds: getBounds([tenor]),
                fwdBounds,
                fwdStartOverride: tenor === '*' ? 0 : fwdBounds.minDays
            });
        }
    } else if (hasCommaAfterFwd) {
        const tenorBounds = getBounds(chronosList);
        for (const fwd of fwdList) {
            const fwdBounds = getTenorRange([fwd], validImm);
            pushResult({
                tenors: chronosList,
                fwdTenors: fwd,
                tenorBounds,
                fwdBounds,
                fwdStartOverride: fwd === '*' ? 0 : fwdBounds.minDays
            });
        }
    } else {
        const fwdBounds = getTenorRange(fwdList, validImm);
        pushResult({
            tenors: chronosList,
            fwdTenors: fwdList,
            tenorBounds: getBounds(chronosList),
            fwdBounds,
            fwdStartOverride: fwdMatch[2] === '*' ? 0 : fwdBounds.minDays
        });
    }

    return result;
};

const validateBeforeFwd = (beforeFwd) => {
    const tokens = beforeFwd.trim().split(/\s+/);
    const tokenCount = tokens.length;

    if (tokenCount === 1) {
        const token = tokens[0];
        if (token === '*' || TENOR_REGEX.test(token)) return true;
        if (token.includes('-')) return validateRange(token);
        if (token.includes(',')) {
            return token.split(',').every((t) => TENOR_REGEX.test(t.trim()));
        }
        return false;
    }

    return tokenCount > 1 && tokens.every((t) => TENOR_REGEX.test(t));
};

export const parseChronosPlot = (text, validImm, isIrsPage, meetingDates) => {
    const trimmedText = text.replace(/^(chronos|trace)\b/i, 'plot');

    const result = checkForImmFwdPlot(trimmedText, validImm, isIrsPage);
    let normalizedText = text
        .toLowerCase()
        .replace(/\s*,\s*/g, ',')
        .trim()
        .replace(/([zmuh]\d{1,2})\s+(\d+(?:\.\d+)?[dwmy])/gi, '$1$2')
        .replace(X_SEPARATOR_REGEX, ' ');

    if (result.isValid) {
        const { imm, tenors } = result;
        const normalizeTenor = (v) => v.replace(/^(\.\d+)/, '0$1');
        const tenor = tenors.map(normalizeTenor).filter((v) => {
            const num = parseFloat(v.replace(/y$/i, ''));
            return num !== imm.y;
        });
        normalizedText = `chronos ${tenor.reverse().join(' ')} fwd ${imm?.label}`;
    }

    const meetingText = normalizedText?.replace(/^(chronos|trace)\b/i, '')?.trim();
    const isMeetingDate = isMeetingDatePattern(meetingText);

    if (isMeetingDate) {
        if (!meetingDates?.length) {
            return [{ isValid: false }];
        }

        const normalizedMeetingText = meetingText.toLowerCase().trim();
        const matchedMeetings = normalizedMeetingText === 'meet'
            ? [...meetingDates]
            : meetingDates.filter((label) => {
                const normalizedLabel = label.toLowerCase();
                const searchTokens = normalizedMeetingText
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(Boolean);

                return searchTokens.every((token) => normalizedLabel.includes(token));
            });

        if (!matchedMeetings.length) {
            return [{ isValid: false }];
        }

        return [
            {
                isValid: true,
                isMeetingDate: true,
                meetingDates: matchedMeetings
            }
        ];
    }

    if (HAS_ALL_REGEX.test(normalizedText)) {
        const hasCurveTenor = /\b\d+[yms]\d+[yms]\b/i.test(normalizedText);

        if (hasCurveTenor) {
            return [{ isValid: false }];
        }

        normalizedText = normalizedText
            .replace(/\bfwd\b.*$/i, '')
            .replace(/\ball\b/i, '')
            .replace(/\s+/g, ' ')
            .trim();
        if (normalizedText === 'chronos') normalizedText = 'chronos *';
        normalizedText += ' fwd *';
    }

    if (
        INVALID_FWD_WITHOUT_SPOT_REGEX.test(normalizedText)
        || INVALID_FWD_WITHOUT_TARGET_REGEX.test(normalizedText)
        || INVALID_CHRONOS_GARBAGE_REGEX.test(normalizedText)
    ) {
        return [{ isValid: false }];
    }

    const isSpread = /\bchronos\b/i.test(normalizedText) && SPREAD_DETECT_REGEX.test(normalizedText);
    let processedText = normalizedText;
    const compactMatch = normalizedText.match(CHRONOS_COMPACT_REGEX);
    if (compactMatch) {
        const [, qualifiers, compactForwards, spot, fwd] = compactMatch;

        if (compactForwards) {
            const pairs = compactForwards
                .split(/\s*,\s*/)
                .map((pair) => {
                    const match = pair.match(COMPACT_PAIR_REGEX);
                    return match ? { fwd: match[1], spot: match[2] } : null;
                })
                .filter(Boolean);

            if (pairs.length && pairs.every((p) => p.spot === pairs[0].spot)) {
                const forwards = pairs.map((p) => p.fwd).join(',');
                processedText = ['chronos', qualifiers, pairs[0].spot, 'fwd', forwards].filter(Boolean).join(' ');
            }
        } else if (spot && fwd) {
            processedText = `chronos ${[qualifiers, fwd, 'fwd', spot].filter(Boolean).join(' ')}`;
        }
    }

    let name = '';
    const structureMatch = processedText.match(STRUCTURE_SPLIT_PATTERN);
    const beforeStructure = structureMatch?.[1]?.trim();

    if (beforeStructure) {
        name = beforeStructure.replace(/\bchronos\b/, '').trim();
        processedText = `chronos ${processedText.slice(beforeStructure.length).trim()}`;
    }

    const fwdMatch = normalizedText.match(FWD_MATCH_REGEX);
    if (fwdMatch && !validateBeforeFwd(fwdMatch[1])) {
        return [{ isValid: false }];
    }

    if (!name && !isIrsPage) {
        return [{ isValid: false }];
    }
    const results = parseChronos(processedText, validImm, isSpread);
    return results.map((item) => ({ ...item, name }));
};

export default parsePlot;
